import React, { createContext, useCallback, useEffect, useMemo } from 'react';
import { useProgramsData } from '@hub/api/queries';
import { useStore } from '@hub/helpers/use-store';
import { useQueries } from '@tanstack/react-query';
import { useFeatureFlagContext } from '@monorepo/feature-flag/client';
import {
  AccountBalance,
  ConsistencyRules,
  ProfitableDaysResult,
} from '@monorepo/types';
import { useAccountData } from '../hooks/api';
import {
  createAccountBalanceQuery,
  createConsistencyRuleQuery,
} from '../hooks/query-creators';
import { getAssetTypeFromAccount } from '../utils/asset-type.utils';
import { CONTEXT_CONFIG } from './constants';
import { DashboardContextValue, DashboardProviderProps } from './types';
import { calculateStatistics } from './utils/statistics-calculator';

/**
 * Dashboard Context
 * Single context for all dashboard data and state management
 */
export const DashboardContext = createContext<
  DashboardContextValue | undefined
>(undefined);

/**
 * Dashboard Provider
 * Provides all dashboard data through a single, clean context with reactive polling
 */
export const DashboardProvider: React.FC<DashboardProviderProps> = ({
  children,
  sourceId,
}) => {
  const {
    dataStore: { accountStore },
  } = useStore();

  // Use React Query hooks directly for live, polling data
  const {
    data: account,
    isLoading: isAccountLoading,
    error: accountError,
    refetch: refetchAccount,
  } = useAccountData(sourceId);

  const {
    data: programsData,
    isLoading: isProgramsLoading,
    error: programsError,
    refetch: refetchPrograms,
  } = useProgramsData();

  // Use account balance and profitable days queries with polling
  // Note: These are query configuration creators, not hooks, so calling them here is safe
  const accountBalanceQueryConfig = createAccountBalanceQuery(
    accountStore,
    account,
  );
  const consistencyRuleQueryConfig = createConsistencyRuleQuery(
    accountStore,
    account,
    ConsistencyRules.ProfitableDays,
  );

  const accountDependentQueries = useQueries({
    queries: [accountBalanceQueryConfig, consistencyRuleQueryConfig],
  });
  const featureFlag = useFeatureFlagContext();

  const [balanceQuery, profitableDaysQuery] = accountDependentQueries;
  const accountBalance = balanceQuery.data as AccountBalance | undefined;
  const profitableDays = profitableDaysQuery.data as
    | ProfitableDaysResult
    | undefined;
  const isBalanceLoading = balanceQuery.isLoading;
  const isProfitableDaysLoading = profitableDaysQuery.isLoading;
  const balanceError = balanceQuery.error;
  const profitableDaysError = profitableDaysQuery.error;

  // Get program data based on the account's programId
  const program = useMemo(() => {
    if (!account?.programId || !programsData) return null;
    return programsData.find((p) => p._id === account.programId) || null;
  }, [account?.programId, programsData]);

  // Determine asset type from account
  const assetType = useMemo(
    () => getAssetTypeFromAccount(account ?? null),
    [account],
  );

  // Derived states
  const isLoading =
    isAccountLoading ||
    isBalanceLoading ||
    isProgramsLoading ||
    isProfitableDaysLoading;
  const error =
    accountError?.message ||
    balanceError?.message ||
    programsError?.message ||
    profitableDaysError?.message ||
    null;

  // Auto-calculate statistics when core data changes
  const statistics = useMemo(() => {
    if (
      CONTEXT_CONFIG.AUTO_CALCULATE_STATISTICS &&
      (account || accountBalance || program)
    ) {
      return calculateStatistics(
        account || null,
        accountBalance || null,
        program,
      );
    }
    return null;
  }, [account, accountBalance, program]);

  // Actions - now use React Query refetch methods
  const refreshAll = useCallback(() => {
    refetchAccount();
    refetchPrograms();
    balanceQuery.refetch();
    profitableDaysQuery.refetch();
  }, [refetchAccount, refetchPrograms, balanceQuery, profitableDaysQuery]);

  const retry = useCallback(() => {
    // Retry only failed operations
    if (accountError) {
      refetchAccount();
    }
    if (balanceError) {
      balanceQuery.refetch();
    }
    if (profitableDaysError) {
      profitableDaysQuery.refetch();
    }
    if (programsError) {
      refetchPrograms();
    }
  }, [
    accountError,
    balanceError,
    profitableDaysError,
    programsError,
    refetchAccount,
    refetchPrograms,
    balanceQuery,
    profitableDaysQuery,
  ]);

  // Note: These update methods are kept for backward compatibility
  // but they won't affect the reactive queries
  const updateAccount = useCallback(() => {
    console.warn(
      'updateAccount is deprecated - data is now managed by React Query',
    );
    refetchAccount();
  }, [refetchAccount]);

  const updateBalance = useCallback(() => {
    console.warn(
      'updateBalance is deprecated - data is now managed by React Query',
    );
    balanceQuery.refetch();
  }, [balanceQuery]);

  const updateProgram = useCallback(() => {
    console.warn(
      'updateProgram is deprecated - data is now managed by React Query',
    );
    refetchPrograms();
  }, [refetchPrograms]);

  const recalculateStatistics = useCallback(() => {
    console.warn(
      'recalculateStatistics is deprecated - statistics are now automatically calculated',
    );
    // Statistics are now automatically recalculated via useMemo
  }, []);

  const contextValue: DashboardContextValue = {
    // State
    account: account || null,
    accountBalance: accountBalance || null,
    program,
    statistics,
    profitableDays: profitableDays || null,
    assetType,
    isLoading,
    isAccountLoading,
    isBalanceLoading,
    isProgramLoading: isProgramsLoading,
    isProfitableDaysLoading,
    error,
    accountError: accountError?.message || null,
    balanceError: balanceError?.message || null,
    programError: programsError?.message || null,
    profitableDaysError: profitableDaysError?.message || null,

    // Actions
    refreshAll,
    retry,
    updateAccount,
    updateBalance,
    updateProgram,
    recalculateStatistics,
  };

  useEffect(() => {
    featureFlag.updateAttributes({
      tsAccountId: account?.tsAccountId,
    });
  }, [account]);

  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  );
};
